import React from "react";
import { 
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell,
  Tooltip, Legend
} from "recharts";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

interface GapAnalysisChartProps {
  formValues?: Record<string, any>;
  categories?: { id: string; label: string; target?: number }[];
  className?: string;
}

export function GapAnalysisChart({ formValues = {}, categories = [], className }: GapAnalysisChartProps) {
  // Default categories for financial gap analysis
  const defaultCategories = [
    { id: 'einkommen', label: 'Einkommen', target: 100 },
    { id: 'ausgaben', label: 'Ausgaben', target: 70 },
    { id: 'sparen', label: 'Sparen', target: 20 },
    { id: 'versicherung', label: 'Versicherung', target: 15 },
    { id: 'vorsorge', label: 'Vorsorge', target: 10 },
  ];

  const cats = categories.length > 0 ? categories : defaultCategories;

  const chartData = cats.map(cat => {
    const actual = Number(formValues[cat.id]) || Math.floor(Math.random() * 80 + 20);
    const target = cat.target || 100;
    const gap = target - actual;
    
    return {
      name: cat.label,
      actual,
      target,
      gap: gap > 0 ? gap : 0,
      status: gap > 10 ? 'warning' : gap > 0 ? 'partial' : 'good',
    };
  });

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3">
          <p className="font-medium mb-1">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}%
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Chart */}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Bar 
            dataKey="actual" 
            name="Aktuell" 
            stackId="a" 
            fill="hsl(var(--primary))"
            radius={[0, 0, 0, 0]}
            animationDuration={800}
          />
          <Bar 
            dataKey="gap" 
            name="Lücke" 
            stackId="a" 
            fill="hsl(var(--destructive) / 0.4)"
            radius={[4, 4, 0, 0]}
            animationDuration={800}
            animationBegin={300}
          />
        </BarChart>
      </ResponsiveContainer>

      {/* Status Cards */}
      <div className="grid grid-cols-1 gap-2">
        {chartData.map((item, index) => (
          <div 
            key={index}
            className={cn(
              "flex items-center justify-between p-3 rounded-lg",
              item.status === 'good' && "bg-chart-5/10",
              item.status === 'partial' && "bg-chart-1/10",
              item.status === 'warning' && "bg-destructive/10"
            )}
          >
            <div className="flex items-center gap-3">
              {item.status === 'good' ? (
                <TrendingUp className="h-4 w-4 text-chart-5" />
              ) : item.status === 'partial' ? (
                <Minus className="h-4 w-4 text-chart-1" />
              ) : (
                <TrendingDown className="h-4 w-4 text-destructive" />
              )}
              <span className="text-sm font-medium">{item.name}</span>
            </div>
            <div className="text-right">
              <span className={cn(
                "text-sm font-bold",
                item.status === 'good' && "text-chart-5",
                item.status === 'partial' && "text-chart-1",
                item.status === 'warning' && "text-destructive"
              )}>
                {item.actual}%
              </span>
              <span className="text-xs text-muted-foreground ml-1">/ {item.target}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
